import React, { useState } from "react";
import { DatePicker } from "antd";
import locale from "antd/es/date-picker/locale/es_ES";
import moment from "moment";
import "moment/locale/es";

/**
 * Selector de la FECHA DE EMISIÓN de un CFDI de nómina — el atributo `Fecha` del
 * comprobante, no la fecha de pago (@FechaPago del complemento Nomina12, que se
 * captura aparte y sale del periodo).
 *
 * El SAT sólo acepta un comprobante cuya `Fecha` tenga a lo más 72 horas de
 * antigüedad al momento del timbre, y nunca una fecha futura. Este componente
 * acota la captura a esa ventana y el backend la vuelve a validar en
 * `erp_backend/Utils/fechaTimbrado.utils.js` (ahí vive el límite real de 72 h).
 *
 * Por qué los topes de aquí NO son 72 h / ahora exactos:
 *
 *  - `MARGEN_ATRAS_HORAS = 70` — dos horas menos que el límite del SAT. El
 *    timbrado masivo corre en segundo plano y puede tardar minutos; una fecha
 *    elegida al filo de las 72 h vencería a media corrida y las últimas líneas
 *    saldrían rechazadas. Es una guarda de UX, no una regla fiscal: por eso el
 *    backend sigue aceptando hasta 72 h y el tiempo que el usuario pase con el
 *    modal abierto nunca se convierte en un rechazo del servidor.
 *
 *  - `MARGEN_ADELANTE_MINUTOS = 5` — el tope superior es `ahora − 5 min`, no
 *    `ahora`. Si el reloj de nuestro servidor va apenas adelantado respecto al
 *    del SAT, un comprobante fechado exactamente "ahora" le cae en el futuro y
 *    lo rechaza.
 *
 * OJO: el valor es un objeto **moment**, nunca dayjs. Los DatePicker de este
 * proyecto son antd v4 (basado en moment) y el panel del calendario truena con
 * dayjs al abrirse (`t.weekday is not a function`).
 *
 * Props:
 *   value      moment|null   fecha seleccionada
 *   onChange   (moment) => void
 *   disabled   bool
 */

// Estas constantes se duplican del backend a propósito: el frontend no puede
// importar de erp_backend. Su contraparte está en Utils/fechaTimbrado.utils.js.
export const HORAS_LIMITE_SAT = 72;
export const MARGEN_ATRAS_HORAS = 70;
export const MARGEN_ADELANTE_MINUTOS = 5;

// Formato exacto en el que el backend espera la fecha (el mismo que se manda al PAC).
export const FORMATO_ENVIO = "YYYY-MM-DDTHH:mm:ss";

const FORMATO_VISIBLE = "DD/MM/YYYY HH:mm";

/** Ventana permitida, recalculada contra el reloj del navegador en cada llamada. */
export function limitesFechaEmision() {
    return {
        min: moment().subtract(MARGEN_ATRAS_HORAS, "hours"),
        max: moment().subtract(MARGEN_ADELANTE_MINUTOS, "minutes"),
    };
}

/** Valor inicial de los selectores: lo más cercano a "ahora" que es seguro timbrar. */
export function fechaEmisionPorDefecto() {
    return moment().subtract(MARGEN_ADELANTE_MINUTOS, "minutes");
}

/** Ajusta una fecha a la ventana permitida. Devuelve { fecha, ajustada }. */
export function ajustarFechaEmision(fecha) {
    if (!fecha) return { fecha: null, ajustada: false };
    const { min, max } = limitesFechaEmision();
    if (fecha.isBefore(min)) return { fecha: min, ajustada: true };
    if (fecha.isAfter(max)) return { fecha: max, ajustada: true };
    return { fecha, ajustada: false };
}

/**
 * Guarda para el submit. Devuelve el mensaje de error, o null si la fecha sirve.
 * Existe porque un modal puede quedarse abierto un buen rato y la fecha que era
 * válida al abrirlo envejece sola.
 */
export function errorFechaEmision(fecha) {
    if (!fecha) return "Indica la fecha de emisión del CFDI";
    const { min, max } = limitesFechaEmision();
    if (fecha.isBefore(min)) {
        return `La fecha de emisión ya quedó fuera de la ventana permitida (el SAT sólo acepta hasta ${HORAS_LIMITE_SAT} horas atrás). Vuelve a elegirla.`;
    }
    if (fecha.isAfter(max)) {
        return "La fecha de emisión no puede ser posterior a la fecha y hora actual";
    }
    return null;
}

/** Serializa al formato que espera el backend. */
export function formatoFechaEmision(fecha) {
    return fecha ? fecha.format(FORMATO_ENVIO) : null;
}

function SelectorFechaEmision({ value, onChange, disabled }) {
    const [aviso, setAviso] = useState(null);
    const { min, max } = limitesFechaEmision();

    const manejarCambio = (fecha) => {
        if (!fecha) {
            setAviso(null);
            onChange(null);
            return;
        }
        // El minuto exacto no se bloquea con `disabledTime` (en antd v4 hay que
        // enumerar horas y minutos deshabilitados y se presta a huecos); en vez
        // de eso se ajusta el valor al límite más cercano y se avisa.
        const { fecha: ajustada, ajustada: seAjusto } = ajustarFechaEmision(fecha);
        setAviso(
            seAjusto
                ? `Se ajustó a ${ajustada.format(FORMATO_VISIBLE)}: es el límite permitido.`
                : null
        );
        onChange(ajustada);
    };

    return (
        <div>
            <DatePicker
                value={value}
                onChange={manejarCambio}
                disabled={disabled}
                locale={locale}
                format={FORMATO_VISIBLE}
                showTime={{ format: "HH:mm" }}
                showNow={false}
                allowClear={false}
                style={{ width: "100%" }}
                placeholder="Selecciona la fecha y hora de emisión"
                status={errorFechaEmision(value) ? "error" : ""}
                disabledDate={(fecha) =>
                    fecha.isBefore(min, "day") || fecha.isAfter(max, "day")
                }
            />
            {aviso && (
                <div style={{ color: "#d46b08", fontSize: 11, marginTop: 3 }}>{aviso}</div>
            )}
            <div style={{ color: "#888", fontSize: 11, marginTop: 3 }}>
                El SAT sólo acepta comprobantes con fecha de hasta {HORAS_LIMITE_SAT} horas
                atrás. Permitido: {min.format(FORMATO_VISIBLE)} a {max.format(FORMATO_VISIBLE)}.
            </div>
        </div>
    );
}

export default SelectorFechaEmision;
